/** Shared helpers for displaying FCC import/sync status (used by the Import
 *  Management page and the dashboard Data Sync widget). */

/** Human-readable label for each import phase value stored in current_phase. */
export const PHASE_LABELS: Record<string, string> = {
  downloading: 'Downloading file…',
  extracting: 'Extracting archive…',
  parsing_hd: 'Parsing licenses…',
  parsing_en: 'Parsing entities…',
  parsing_lo: 'Parsing locations…',
  parsing_sf: 'Parsing frequencies…',
  inserting_entities: 'Importing entities…',
  inserting_licenses: 'Importing licenses…',
  inserting_locations: 'Importing locations…',
  inserting_frequencies: 'Importing frequencies…',
  linking: 'Linking records…',
  pruning: 'Pruning out-of-state records…',
  fetching_applications: 'Fetching applications…',
  inserting_applications: 'Importing applications…',
  completed: 'Finalizing…',
}

export function phaseLabel(phase: string | null): string {
  if (!phase) return 'Working…'
  return PHASE_LABELS[phase] ?? phase
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

export function formatDuration(secs: number | null): string {
  if (secs === null) return '—'
  if (secs < 60) return `${secs}s`
  if (secs < 3600) return `${Math.floor(secs / 60)}m`
  const d = Math.floor(secs / 86400)
  const h = Math.floor((secs % 86400) / 3600)
  const m = Math.floor((secs % 3600) / 60)
  if (d > 0) return `${d}d ${h}h ${m}m`
  return `${h}h ${m}m`
}

/**
 * Parse a server timestamp into a Date, treating naive timestamps (no timezone
 * designator) as UTC. The backend emits naive-UTC datetimes; parsing those with
 * `new Date()` would interpret them as local time and skew elapsed/ETA math by
 * the viewer's UTC offset (e.g. ~6–7h in Mountain Time → negative elapsed).
 */
export function parseServerDate(s: string | null | undefined): Date | null {
  if (!s) return null
  const hasTz = /([zZ])|([+-]\d{2}:?\d{2})$/.test(s)
  return new Date(hasTz ? s : `${s}Z`)
}

/** Live elapsed time since startedAt, formatted compactly (e.g. "3m 07s" / "1h 04m"). */
export function formatElapsed(startedAt: string | null): string {
  const start = parseServerDate(startedAt)
  if (!start) return ''
  const totalSecs = Math.floor((Date.now() - start.getTime()) / 1000)
  if (totalSecs < 0) return ''
  const h = Math.floor(totalSecs / 3600)
  const m = Math.floor((totalSecs % 3600) / 60)
  const s = totalSecs % 60
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`
  return `${m}m ${String(s).padStart(2, '0')}s`
}
